import { X, Zap } from 'lucide-react';
import { UserRole } from '@types';
import { SUBSCRIPTION_TIERS, STORAGE_ADDONS, formatStorageSize } from '@config/subscription';
import { cn } from '@utils/classNames';
import { ModalShell } from '@components/ui/ModalShell';

export type UpgradeModalReason =
  | 'storage_limit'
  | 'file_limit'
  | 'youtube_limit'
  | 'version_limit'
  | 'beatpack_limit';

interface UpgradeModalProps {
  currentRole: UserRole;
  reason: UpgradeModalReason;
  recommendedRole?: UserRole;
  onClose: () => void;
  onUpgrade?: (role: UserRole) => void;
  onPurchaseAddon?: (addonId: string) => void;
  /** Defaults to in-app `/pricing` */
  pricingHref?: string;
}

function getQuotaCopy(
  reason: UpgradeModalReason,
  currentTierName: string,
  storageLimitLabel: string,
  maxFiles: number
): { title: string; description: string } {
  switch (reason) {
    case 'storage_limit':
      return {
        title: 'Storage Limit Reached',
        description: `You've reached your ${storageLimitLabel} storage limit on the ${currentTierName} plan.`,
      };
    case 'file_limit':
      return {
        title: 'File Limit Reached',
        description: `You've reached your ${maxFiles} file limit on the ${currentTierName} plan.`,
      };
    case 'youtube_limit':
      return {
        title: 'YouTube Upload Limit Reached',
        description: `Your ${currentTierName} plan's monthly YouTube uploads are used up. Upgrade for more uploads — see plans on the pricing page.`,
      };
    case 'version_limit':
      return {
        title: 'Version Creation Limit Reached',
        description: `You've hit the monthly version creation limit on the ${currentTierName} plan. Upgrade to create more tagged versions.`,
      };
    case 'beatpack_limit':
      return {
        title: 'Beat Pack Limit Reached',
        description: `The Free plan includes one beat pack. Upgrade to share unlimited beat packs.`,
      };
    default:
      return {
        title: 'Upgrade required',
        description: `You've reached a limit on the ${currentTierName} plan.`,
      };
  }
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({
  currentRole,
  reason,
  recommendedRole,
  onClose,
  onUpgrade,
  onPurchaseAddon,
  pricingHref = '/pricing',
}) => {
  const currentTier = SUBSCRIPTION_TIERS[currentRole];
  const { title, description } = getQuotaCopy(
    reason,
    currentTier.name,
    formatStorageSize(currentTier.storageLimit),
    currentTier.maxFiles
  );

  const upgradeTiers = [UserRole.FREE, UserRole.STARTER, UserRole.PRO, UserRole.PREMIUM].filter((role) => {
    const tierIndex = [UserRole.FREE, UserRole.STARTER, UserRole.PRO, UserRole.PREMIUM].indexOf(role);
    const currentIndex = [UserRole.FREE, UserRole.STARTER, UserRole.PRO, UserRole.PREMIUM].indexOf(currentRole);
    return tierIndex > currentIndex;
  });

  const showStorageAddons = reason === 'storage_limit';

  return (
    <ModalShell
      onClose={onClose}
      panelClassName="max-w-4xl max-h-[90dvh] flex flex-col rounded-2xl"
    >
      <div
        className="relative w-full max-w-4xl max-h-[90dvh] bg-dark-secondary border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="relative bg-gradient-to-r from-primary/20 to-beat/20 p-6 border-b border-white/10">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 min-h-[44px] min-w-[44px] hover:bg-white/10 rounded-lg transition-colors touch-manipulation"
          >
            <X className="w-5 h-5 text-white/60" />
          </button>

          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary/20 rounded-lg">
              <Zap className="w-6 h-6 text-primary" />
            </div>
            <h2 className="font-dela text-2xl text-white">{title}</h2>
          </div>
          <p className="text-white/60">{description}</p>
          <a
            href={pricingHref}
            className="inline-block mt-3 text-sm font-semibold text-primary hover:text-primary/90 underline underline-offset-2"
          >
            Compare all plans & pricing
          </a>
        </div>

        <div className="p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] overflow-y-auto">
          <div className="mb-6">
            <h3 className="font-dela text-lg text-white mb-4">Upgrade Your Plan</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upgradeTiers.map((role) => {
                const tier = SUBSCRIPTION_TIERS[role];
                const isRecommended = role === recommendedRole;

                return (
                  <div
                    key={role}
                    className={cn(
                      'relative p-5 rounded-xl border-2 transition-all',
                      isRecommended
                        ? 'border-primary bg-primary/10'
                        : 'border-white/10 bg-white/5 hover:border-primary/50'
                    )}
                  >
                    {isRecommended && (
                      <div className="absolute -top-3 left-4 px-3 py-1 bg-primary text-black text-xs font-bold rounded-full">
                        Recommended
                      </div>
                    )}

                    <div className="mb-4">
                      <h4 className="font-dela text-xl text-white mb-1">{tier.name}</h4>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-primary">€{tier.price}</span>
                        <span className="text-sm text-white/60">/month</span>
                      </div>
                    </div>

                    <div className="space-y-2 mb-5">
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        <span className="text-white/80">{formatStorageSize(tier.storageLimit)} Storage</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                        <span className="text-white/80">Up to {tier.maxFiles} files</span>
                      </div>
                      {tier.features.slice(2, 4).map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary/60" />
                          <span className="text-white/60">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        onUpgrade?.(role);
                        onClose();
                      }}
                      className={cn(
                        'w-full py-2.5 rounded-lg font-inter font-semibold transition-colors',
                        isRecommended
                          ? 'bg-primary hover:bg-primary-dark text-black'
                          : 'bg-white/10 hover:bg-white/20 text-white'
                      )}
                    >
                      Upgrade to {tier.name}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {showStorageAddons && (
            <div>
              <h3 className="font-dela text-lg text-white mb-4">Or Add More Storage</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {STORAGE_ADDONS.map((addon) => (
                  <div
                    key={addon.id}
                    className="p-4 rounded-xl border border-white/10 bg-white/5 hover:border-primary/50 transition-all"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-inter font-bold text-white">{addon.name}</span>
                      <span className="text-sm text-primary font-bold">€{addon.price}/mo</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        onPurchaseAddon?.(addon.id);
                        onClose();
                      }}
                      className="w-full py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg font-inter font-semibold transition-colors text-sm"
                    >
                      Add Storage
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </ModalShell>
  );
};
